import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';

import { useTranslation } from '@common/hooks/translation';
import { SetBelgianVerificationError } from '@app/dev/actions/terminal';

import { BELGIAN_ERROR_MESSAGES } from './messages';
import './style.scss';

// Belgian eID law is trilingual (EN/FR/NL). Strings are intentionally NOT run
// through `t()` — every variant always renders regardless of the active skin
// language, since this gate catches users in any locale.
const LOCALES = [
  {
    code: 'EN',
    title: 'Identification required',
    body: 'Please insert your electronic ID card to use this terminal.',
  },
  {
    code: 'FR',
    title: 'Identification requise',
    body: "Veuillez insérer votre carte d'identité électronique pour utiliser ce terminal.",
  },
  {
    code: 'NL',
    title: 'Identificatie vereist',
    body: 'Gelieve uw elektronische identiteitskaart in te steken om deze terminal te gebruiken.',
  },
];

// Single shared rotation drives BOTH the panel's title/body and the error
// banner's title/body. 10s gives readers comfortable time to absorb the
// denser error message in their language without feeling like the screen
// is moving too fast.
const ROTATION_INTERVAL_MS = 10000;
// Auto-dismiss window for the whole error block. ~45s = roughly 1.5 full
// trilingual rotation cycles at 10s/locale, so the user is guaranteed to see
// every language at least once before the banner clears.
const ERROR_AUTO_HIDE_MS = 45000;

// ISO codes per LOCALES entry — used to pull the matching variant out of
// BELGIAN_ERROR_MESSAGES so the error banner stays in lock-step with whichever
// locale the panel above is currently showing.
const LOCALE_ISO_KEYS = ['en', 'fr', 'nl'];

// Builds the [{ code, title, body }] tuple the error banner expects from the
// trilingual messages dictionary. Falls back to the `unavailable` category if
// the resolved error key isn't a known entry (defensive — should never hit
// once the Code map is up to date).
const buildErrorLocales = (errorKey) => {
  const messages = BELGIAN_ERROR_MESSAGES[errorKey] || BELGIAN_ERROR_MESSAGES.unavailable;
  return LOCALES.map((l, i) => ({
    code: l.code,
    title: messages[LOCALE_ISO_KEYS[i]].title,
    body: messages[LOCALE_ISO_KEYS[i]].body,
  }));
};

const VerificationBlocker = () => {
  const t = useTranslation();
  const dispatch = useDispatch();

  // Single locale index shared by the panel AND the error banner. When the
  // user clicks a lang pill or the timer ticks, both blocks crossfade to the
  // same language in sync.
  const [localeIndex, setLocaleIndex] = useState(0);
  const timerRef = useRef(null);

  const startRotation = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setLocaleIndex((i) => (i + 1) % LOCALES.length);
    }, ROTATION_INTERVAL_MS);
  };

  useEffect(() => {
    startRotation();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Manual switch from a pill click. Resets the rotation timer so the user
  // gets a full read window before the next auto-advance.
  const onLocaleClick = (i) => {
    setLocaleIndex(i);
    startRotation();
  };

  // Belgian eID error category resolved from `window.belgianVerificationResult`
  // → see `@app/dev/helpers/terminalSpecific.jsx`. `null` ⇒ no error banner.
  const belgianErrorKey = useSelector((state) => state.terminal.verification.belgianErrorKey);
  const hasError = !!belgianErrorKey;
  const errorLocales = hasError ? buildErrorLocales(belgianErrorKey) : [];

  // Auto-hide the whole error block after `ERROR_AUTO_HIDE_MS`. Restarts on
  // a fresh error (different category mid-display) so the user always gets
  // a full read window before the banner disappears. `verificationSuccess`
  // still clears the key explicitly for an instant dismiss on success.
  useEffect(() => {
    if (!belgianErrorKey) return undefined;
    const timeout = setTimeout(() => {
      dispatch(SetBelgianVerificationError(null));
    }, ERROR_AUTO_HIDE_MS);
    return () => clearTimeout(timeout);
  }, [belgianErrorKey, dispatch]);

  return (
    <div className={classnames('verification-blocker', { 'has-error': hasError })}>
      <div className="vb-bg" aria-hidden="true">
        <span className="vb-glow vb-glow-1" />
        <span className="vb-glow vb-glow-2" />
        <span className="vb-glow vb-glow-3" />
        <span className="vb-grid" />
      </div>

      <div className="vb-stack">
        {/* Shared language switcher — owns BOTH the panel's rotation and the
            error banner's rotation. Pulled out of the blocks so the user
            has one consistent control surface at the top regardless of
            whether the error block is showing or not. */}
        <div className="vb-langs-shared">
          {LOCALES.map((v, i) => (
            <button
              key={v.code}
              type="button"
              className={classnames('vb-lang', { 'is-active': i === localeIndex })}
              onClick={() => onLocaleClick(i)}
              aria-label={v.code}
              aria-pressed={i === localeIndex}
            >
              {v.code}
            </button>
          ))}
        </div>

        <div className="vb-panel">
          <div className="vb-icon-wrap">
            <span className="vb-pulse vb-pulse-1" />
            <span className="vb-pulse vb-pulse-2" />
            <span className="vb-pulse vb-pulse-3" />
            <span className="vb-icon-disc">
              <i className="global-icon icon-rfid-scan vb-icon" />
            </span>
          </div>

          <div className="vb-i18n" aria-live="polite">
            {LOCALES.map((v, i) => (
              <div
                key={v.code}
                className={classnames('vb-i18n-pane', { 'is-active': i === localeIndex })}
                aria-hidden={i !== localeIndex}
              >
                <h1 className="vb-title">{v.title}</h1>
                <p className="vb-subtitle">{v.body}</p>
              </div>
            ))}
          </div>

          <div className="vb-card-slot" aria-hidden="true">
            <span className="vb-card">
              <span className="vb-card-header">
                <span className="vb-card-flag" />
                <span className="vb-card-header-bar" />
              </span>
              <span className="vb-card-photo">
                <span className="vb-card-photo-head" />
                <span className="vb-card-photo-shoulders" />
              </span>
              <span className="vb-card-line vb-card-line-1" />
              <span className="vb-card-line vb-card-line-2" />
              <span className="vb-card-line vb-card-line-3" />
              <span className="vb-card-seal" />
            </span>
            <span className="vb-slot" />
            <span className="vb-slot-glow" />
          </div>

          <div className="vb-waiting">
            <span className="vb-dot" />
            <span className="vb-dot" />
            <span className="vb-dot" />
            <span className="vb-waiting-text">{t('Waiting for ID card')}</span>
          </div>
        </div>

        {hasError && (
          <div className="vb-error" role="alert" aria-live="assertive">
            <span className="vb-error-glow" aria-hidden="true" />

            <div className="vb-error-head">
              <div className="vb-error-icon-wrap" aria-hidden="true">
                <span className="vb-error-pulse" />
                <span className="vb-error-icon-disc">
                  <i className="global-icon icon-warning-transparent vb-error-icon" />
                </span>
              </div>
            </div>

            {/* Grid-stack crossfade — every locale pane occupies grid cell 1/1
                so the container auto-sizes to the longest variant. The shared
                `localeIndex` from the top switcher drives `is-active`. */}
            <div className="vb-error-i18n">
              {errorLocales.map((v, i) => (
                <div
                  key={v.code}
                  className={classnames('vb-error-pane', { 'is-active': i === localeIndex })}
                  aria-hidden={i !== localeIndex}
                >
                  <span className="vb-error-title">{v.title}</span>
                  <span className="vb-error-body">{v.body}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationBlocker;
